import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { LogOut, Package, Inbox, LayoutGrid, RefreshCw, CreditCard } from "lucide-react";

const STATUSES = ["new", "in_progress", "completed", "cancelled"];
const STATUS_COLORS = {
  new: "hsl(190 90% 40%)",
  in_progress: "hsl(40 90% 45%)",
  completed: "hsl(140 60% 40%)",
  cancelled: "hsl(0 84% 55%)",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("requests");
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, c, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/requests"),
        api.get("/admin/contacts"),
        api.get("/admin/payments"),
      ]);
      setStats(s.data);
      setRequests(r.data);
      setContacts(c.data);
      setPayments(p.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("bf_token");
        nav("/admin");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("bf_token")) { nav("/admin"); return; }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/requests/${id}`, { status });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Status → ${status}`);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch {
      toast.error("Update failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("bf_token");
    nav("/admin");
  };

  const StatCard = ({ label, value, icon: Icon }) => (
    <div className="border border-border p-6 bg-card">
      <div className="flex items-center justify-between text-muted-foreground mb-4">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em]">{label}</span>
        <Icon size={16} />
      </div>
      <div className="font-display font-black text-4xl tracking-tighter tabular-nums">{value ?? "—"}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 border-b md:border-b-0 md:border-r border-border md:min-h-screen p-6 flex md:flex-col justify-between">
        <div>
          <div className="font-display font-black text-sm tracking-tighter uppercase mb-10">
            BioFleet<span className="text-primary">/</span>Admin
          </div>
          <nav className="flex md:flex-col gap-1">
            {[
              { id: "requests", label: "Requests", icon: Package },
              { id: "payments", label: "Payments", icon: CreditCard },
              { id: "contacts", label: "Messages", icon: Inbox },
              { id: "overview", label: "Overview", icon: LayoutGrid },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-3 px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
                    tab === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </nav>
        </div>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="hidden md:flex items-center gap-3 px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive transition-colors"
        >
          <LogOut size={15} /> Sign Out
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10 max-w-full overflow-hidden">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display font-black text-2xl md:text-4xl tracking-tighter uppercase">
            {tab === "requests" ? "Service Requests" : tab === "payments" ? "Payments" : tab === "contacts" ? "Messages" : "Overview"}
          </h1>
          <button
            data-testid="refresh-btn"
            onClick={load}
            className="flex items-center gap-2 border border-border px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] hover:border-primary hover:text-primary transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatCard label="Total Requests" value={stats?.total} icon={Package} />
          <StatCard label="New" value={stats?.new} icon={Inbox} />
          <StatCard label="Completed" value={stats?.completed} icon={LayoutGrid} />
          <StatCard label="Revenue" value={stats ? `$${Number(stats.revenue).toFixed(0)}` : undefined} icon={CreditCard} />
          <StatCard label="Messages" value={stats?.contacts} icon={Inbox} />
        </div>

        {tab === "overview" && stats && (
          <div className="border border-border p-6 bg-card">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Requests by Service</span>
            <div className="h-72 mt-6">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.by_service} margin={{ left: -20 }}>
                  <XAxis dataKey="service" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <Bar dataKey="count" fill="hsl(225 85% 35%)">
                    {stats.by_service.map((_, i) => <Cell key={i} fill="hsl(225 85% 35%)" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {tab === "requests" && (
          <div data-testid="requests-table" className="border border-border overflow-x-auto scrollbar-thin bg-card">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr className="border-b border-border text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">
                  {["Name", "Service", "Contact", "Route", "Date", "Status"].map((h) => (
                    <th key={h} className="px-4 py-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center font-mono text-sm text-muted-foreground">No requests yet.</td></tr>
                )}
                {requests.map((r) => (
                  <tr key={r.id} data-testid={`request-row-${r.id}`} className="border-b border-border hover:bg-secondary/40 transition-colors">
                    <td className="px-4 py-4 font-mono text-sm font-medium">{r.name}</td>
                    <td className="px-4 py-4 font-mono text-xs text-primary">{r.service}</td>
                    <td className="px-4 py-4 font-mono text-xs text-muted-foreground">
                      <div>{r.email}</div><div>{r.phone}</div>
                    </td>
                    <td className="px-4 py-4 font-mono text-xs text-muted-foreground max-w-[180px] truncate">
                      {r.pickup_location || "—"} → {r.dropoff_location || "—"}
                    </td>
                    <td className="px-4 py-4 font-mono text-xs text-muted-foreground">{r.preferred_date || "—"}</td>
                    <td className="px-4 py-4">
                      <select
                        data-testid={`status-select-${r.id}`}
                        value={r.status}
                        onChange={(e) => updateStatus(r.id, e.target.value)}
                        className="bg-transparent border border-border px-2 py-1.5 font-mono text-[11px] uppercase tracking-wider outline-none focus:border-primary"
                        style={{ color: STATUS_COLORS[r.status] }}
                      >
                        {STATUSES.map((s) => <option key={s} value={s} className="text-foreground">{s.replace("_", " ")}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "payments" && (
          <div data-testid="payments-table" className="border border-border overflow-x-auto scrollbar-thin bg-card">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <thead>
                <tr className="border-b border-border text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">
                  {["Date", "Item", "Type", "Email", "Amount", "Status"].map((h) => (
                    <th key={h} className="px-4 py-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center font-mono text-sm text-muted-foreground">No payments yet.</td></tr>
                )}
                {payments.map((p) => (
                  <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-b border-border hover:bg-secondary/40 transition-colors">
                    <td className="px-4 py-4 font-mono text-xs text-muted-foreground">{(p.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-4 font-mono text-sm">{p.label || p.service || "Payment"}</td>
                    <td className="px-4 py-4 font-mono text-xs text-primary uppercase">{p.payment_type}</td>
                    <td className="px-4 py-4 font-mono text-xs text-muted-foreground">{p.customer_email || "—"}</td>
                    <td className="px-4 py-4 font-mono text-sm tabular-nums">${Number(p.amount).toFixed(2)}</td>
                    <td className="px-4 py-4">
                      <span
                        className="font-mono text-[10px] uppercase tracking-wider border px-2 py-1"
                        style={{
                          color: p.payment_status === "paid" ? "hsl(140 60% 40%)" : p.payment_status === "pending" ? "hsl(40 90% 45%)" : "hsl(0 84% 55%)",
                          borderColor: "hsl(var(--border))",
                        }}
                      >
                        {p.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "contacts" && (
          <div data-testid="contacts-list" className="space-y-4">
            {contacts.length === 0 && !loading && (
              <p className="font-mono text-sm text-muted-foreground">No messages yet.</p>
            )}
            {contacts.map((c) => (
              <div key={c.id} className="border border-border p-6 bg-card">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-sm font-medium">{c.name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{c.email}</span>
                </div>
                <p className="font-mono text-sm text-muted-foreground">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
